import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const MarriageCompatibilityNumerologyCalculator = () => {
  const [person1, setPerson1] = useState({
    name: '',
    birthDate: '',
    marriageDate: '',
    relationshipType: 'marriage'
  });
  const [person2, setPerson2] = useState({
    name: '',
    birthDate: '',
    marriageDate: '',
    relationshipType: 'marriage'
  });
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handlePerson1Change = (field, value) => {
    setPerson1(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handlePerson2Change = (field, value) => {
    setPerson2(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const calculateMarriageCompatibility = () => {
    const validatePerson = (person) => {
      if (!person.name || !person.birthDate || !person.marriageDate) {
        return false;
      }
      return true;
    };

    if (!validatePerson(person1) || !validatePerson(person2)) {
      setError('Please enter all required information for both individuals');
      return;
    }

    // Calculate Life Path Numbers
    const calculateLifePath = (birthDate) => {
      const birthDateNumbers = birthDate.split('-').join('');
      let lifePath = birthDateNumbers.split('').reduce((acc, num) => acc + parseInt(num), 0);
      while (lifePath > 9) {
        lifePath = lifePath.toString().split('').reduce((acc, num) => acc + parseInt(num), 0);
      }
      return lifePath;
    };

    // Calculate Marriage Number
    const calculateMarriageNumber = (marriageDate) => {
      const marriageDateNumbers = marriageDate.split('-').join('');
      let marriageNumber = marriageDateNumbers.split('').reduce((acc, num) => acc + parseInt(num), 0);
      while (marriageNumber > 9) {
        marriageNumber = marriageNumber.toString().split('').reduce((acc, num) => acc + parseInt(num), 0);
      }
      return marriageNumber;
    };

    const person1LifePath = calculateLifePath(person1.birthDate);
    const person2LifePath = calculateLifePath(person2.birthDate);
    const marriageNumber = calculateMarriageNumber(person1.marriageDate);

    // Calculate Compatibility Scores
    const compatibilityScores = {
      partnership: Math.abs(person1LifePath - person2LifePath) < 2 ? 90 : 75,
      commitment: person1LifePath + person2LifePath > 15 ? 85 : 70,
      financial: Math.abs(person1LifePath - person2LifePath) < 3 ? 80 : 65,
      family: person1LifePath % 2 === person2LifePath % 2 ? 90 : 75,
      longevity: Math.abs(person1LifePath - marriageNumber) < 2 && Math.abs(person2LifePath - marriageNumber) < 2 ? 95 : 80
    };

    // Calculate Overall Compatibility
    const overallCompatibility = Object.values(compatibilityScores).reduce(
      (acc, score) => acc + score,
      0
    ) / Object.values(compatibilityScores).length;

    // Prepare chart data
    const compatibilityChartData = {
      labels: ['Partnership', 'Commitment', 'Financial', 'Family', 'Longevity'],
      datasets: [
        {
          label: 'Marriage Compatibility Scores',
          data: Object.values(compatibilityScores),
          backgroundColor: 'rgba(79, 70, 229, 0.5)',
          borderColor: 'rgba(79, 70, 229, 1)',
          borderWidth: 1
        }
      ]
    };

    setResults({
      person1: {
        name: person1.name,
        birthDate: person1.birthDate,
        lifePath: person1LifePath
      },
      person2: {
        name: person2.name,
        birthDate: person2.birthDate,
        lifePath: person2LifePath
      },
      marriageNumber,
      compatibilityScores,
      overallCompatibility: Math.round(overallCompatibility)
    });

    setChartData(compatibilityChartData);
  };

  return (
    <div className="math-calculators max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Helmet>
        <title>Marriage Compatibility Numerology Calculator | Calculate Your Marriage Compatibility</title>
        <meta name="description" content="Calculate your marriage compatibility using numerology. Understand your partnership potential and long-term relationship prospects." />
        <meta name="keywords" content="marriage compatibility, numerology calculator, marriage numerology, relationship compatibility, wedding numerology" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://yourwebsite.com/calculator/marriage-compatibility" />
        <meta property="og:title" content="Marriage Compatibility Numerology Calculator | Calculate Your Marriage Compatibility" />
        <meta property="og:description" content="Calculate your marriage compatibility using numerology. Understand your partnership potential and long-term relationship prospects." />
        <meta property="og:image" content="https://yourwebsite.com/images/marriage-compatibility-calculator.png" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://yourwebsite.com/calculator/marriage-compatibility" />
        <meta property="twitter:title" content="Marriage Compatibility Numerology Calculator | Calculate Your Marriage Compatibility" />
        <meta property="twitter:description" content="Calculate your marriage compatibility using numerology. Understand your partnership potential and long-term relationship prospects." />
        <meta property="twitter:image" content="https://yourwebsite.com/images/marriage-compatibility-calculator.png" />
      </Helmet>

      <div className="calculator-header mb-12">
        <div className="flex items-center gap-4">
          <div className="header-icon text-4xl">💍</div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Marriage Compatibility Numerology Calculator</h1>
            <p className="text-lg text-purple-600 font-semibold">Calculate Your Marriage Compatibility</p>
          </div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="calculator-form max-w-4xl mx-auto bg-white rounded-xl shadow-lg p-8"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <h3 className="text-xl font-semibold mb-4">Partner 1 Information</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="person1Name">
                  Name:
                </label>
                <input
                  type="text"
                  id="person1Name"
                  name="person1Name"
                  value={person1.name}
                  onChange={(e) => handlePerson1Change('name', e.target.value)}
                  className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                  placeholder="Enter partner 1's name"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="person1BirthDate">
                  Birth Date:
                </label>
                <input
                  type="date"
                  id="person1BirthDate"
                  name="person1BirthDate"
                  value={person1.birthDate}
                  onChange={(e) => handlePerson1Change('birthDate', e.target.value)}
                  className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="person1MarriageDate">
                  Marriage Date:
                </label>
                <input
                  type="date"
                  id="person1MarriageDate"
                  name="person1MarriageDate"
                  value={person1.marriageDate}
                  onChange={(e) => handlePerson1Change('marriageDate', e.target.value)}
                  className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                />
              </div>
            </div>
          </div>
          <div>
            <h3 className="text-xl font-semibold mb-4">Partner 2 Information</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="person2Name">
                  Name:
                </label>
                <input
                  type="text"
                  id="person2Name"
                  name="person2Name"
                  value={person2.name}
                  onChange={(e) => handlePerson2Change('name', e.target.value)}
                  className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                  placeholder="Enter partner 2's name"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="person2BirthDate">
                  Birth Date:
                </label>
                <input
                  type="date"
                  id="person2BirthDate"
                  name="person2BirthDate"
                  value={person2.birthDate}
                  onChange={(e) => handlePerson2Change('birthDate', e.target.value)}
                  className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="person2MarriageDate">
                  Marriage Date:
                </label>
                <input
                  type="date"
                  id="person2MarriageDate"
                  name="person2MarriageDate"
                  value={person2.marriageDate}
                  onChange={(e) => handlePerson2Change('marriageDate', e.target.value)}
                  className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                />
              </div>
            </div>
          </div>
        </div>

        <button
          onClick={calculateMarriageCompatibility}
          className="w-full bg-purple-500 hover:bg-purple-600 text-white font-bold py-3 px-4 rounded focus:outline-none focus:bg-white mt-8"
        >
          Calculate Marriage Compatibility
        </button>

        {error && (
          <div className="text-red-500 text-center mt-4">
            {error}
          </div>
        )}

        {results && (
          <div className="results-section mt-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Marriage Compatibility Analysis</h3>
            
            <div className="space-y-12">
              <div className="bg-white rounded-xl shadow-lg p-8">
                <div className="space-y-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                      <h4 className="font-medium mb-4">Partner 1 Details</h4>
                      <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-600">Name:</span>
                          <span className="font-medium text-purple-600">{results.person1.name}</span>
                        </div>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-600">Birth Date:</span>
                          <span className="font-medium text-purple-600">{results.person1.birthDate}</span>
                        </div>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                        <h5 className="font-medium mb-2">Life Path Number:</h5>
                        <p className="text-purple-600 text-2xl text-center">{results.person1.lifePath}</p>
                      </div>
                    </div>
                    <div>
                      <h4 className="font-medium mb-4">Partner 2 Details</h4>
                      <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-600">Name:</span>
                          <span className="font-medium text-purple-600">{results.person2.name}</span>
                        </div>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-600">Birth Date:</span>
                          <span className="font-medium text-purple-600">{results.person2.birthDate}</span>
                        </div>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                        <h5 className="font-medium mb-2">Life Path Number:</h5>
                        <p className="text-purple-600 text-2xl text-center">{results.person2.lifePath}</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                    <h5 className="font-medium mb-4">Marriage Number:</h5>
                    <div className="flex items-center justify-center">
                      <div className="text-4xl font-bold text-purple-600">{results.marriageNumber}</div>
                    </div>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                    <h5 className="font-medium mb-4">Overall Marriage Compatibility Score:</h5>
                    <div className="flex items-center justify-center">
                      <div className="text-4xl font-bold text-purple-600">{results.overallCompatibility}%</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Compatibility Scores Visualization */}
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h4 className="text-xl font-semibold mb-6">Marriage Compatibility Scores</h4>
                <div className="h-96 bg-gray-50 rounded-xl p-6">
                  <Bar
                    data={chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        title: {
                          display: true,
                          text: 'Marriage Compatibility Analysis',
                          color: '#4F46E5',
                          font: {
                            size: 16
                          }
                        },
                        legend: {
                          display: true,
                          position: 'bottom',
                          labels: {
                            color: '#4B5563',
                            font: {
                              size: 12
                            }
                          }
                        }
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          ticks: {
                            color: '#4B5563'
                          },
                          grid: {
                            color: '#E5E7EB'
                          }
                        },
                        x: {
                          ticks: {
                            color: '#4B5563'
                          },
                          grid: {
                            color: '#E5E7EB'
                          }
                        }
                      },
                      animation: {
                        duration: 1000,
                        easing: 'easeInOutQuart'
                      }
                    }}
                  />
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-8">
                <h4 className="text-xl font-semibold mb-6">Compatibility Breakdown</h4>
                <div className="space-y-6">
                  {Object.entries(results.compatibilityScores).map(([type, score]) => (
                    <div key={type} className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-gray-600">{type.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:</span>
                        <span className="text-purple-600 font-medium">{score}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div
                          className="bg-purple-500 h-2.5 rounded-full transition-all duration-500"
                          style={{ width: `${score}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </motion.div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Compatibility Types <span className="ml-2 text-2xl">📚</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Partnership Compatibility</h3>
              <p className="text-gray-700">
                Measures the strength of the partnership and ability to work together as a team.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Commitment Compatibility</h3>
              <p className="text-gray-700">
                Evaluates the level of commitment and dedication in the relationship.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Financial Compatibility</h3>
              <p className="text-gray-700">
                Assesses compatibility in financial matters and money management.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Family Compatibility</h3>
              <p className="text-gray-700">
                Evaluates the ability to build and maintain a family unit.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Longevity Compatibility</h3>
              <p className="text-gray-700">
                Measures the potential for a long-lasting and successful marriage.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          How to Use <span className="ml-2 text-2xl">📋</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Follow these steps to calculate marriage compatibility:
          </p>
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Step 1: Enter Information</h3>
              <p className="text-gray-700">
                Enter the names, birth dates, and marriage date for both partners.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Step 2: Calculate</h3>
              <p className="text-gray-700">
                Click the "Calculate Marriage Compatibility" button to see the results.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Step 3: Understand Results</h3>
              <p className="text-gray-700">
                Review the compatibility scores and bar chart to understand the strengths and areas for improvement in your marriage.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          FAQ <span className="ml-2 text-2xl">❓</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="space-y-8">
            <div>
              <h3 className="text-lg font-semibold mb-2">What is Marriage Compatibility Numerology?</h3>
              <p className="text-gray-700">
                Marriage Compatibility Numerology uses numerology principles to analyze compatibility between two individuals in a marriage based on their birth dates and marriage date.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2">How is marriage compatibility calculated?</h3>
              <p className="text-gray-700">
                The calculator analyzes five key areas of marriage compatibility: partnership, commitment, financial, family, and longevity.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2">What does the marriage number mean?</h3>
              <p className="text-gray-700">
                The marriage number is calculated from the marriage date and provides insights into the overall energy and potential of the marriage.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Features & Benefits <span className="ml-2 text-2xl">✨</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Key Features</h3>
              <ul className="list-disc pl-8 space-y-3">
                <li>Marriage-specific compatibility analysis</li>
                <li>Visual compatibility bar chart</li>
                <li>Detailed compatibility breakdown</li>
                <li>Life path number analysis</li>
                <li>Marriage number calculation</li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Benefits</h3>
              <ul className="list-disc pl-8 space-y-3">
                <li>Understand marriage dynamics</li>
                <li>Identify areas of strength</li>
                <li>Discover areas for improvement</li>
                <li>Enhance marriage planning</li>
                <li>Support marriage growth</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarriageCompatibilityNumerologyCalculator;
